/**
 * Avatar (31/10/2026) : la photo de profil d'une personne, ou à défaut ses initiales sur la couleur de la marque.
 * Réutilisable partout où une personne est nommée (menu, fiches, dossiers, tableau de bord).
 *
 * - `photo` : l'adresse renvoyée par l'API (`/api/photos/:id?v=…`, lecture authentifiée), ou `null`.
 * - Décoratif par défaut (le nom est écrit à côté) : `alt=""`. Passer `decoratif={false}` quand l'avatar est seul.
 * - Si l'image ne se charge pas (photo retirée entre-temps, droits), on retombe sur les initiales.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, Trash2 } from "lucide-react";
import { api, ErreurApi, type Moi } from "../api";
import { Bouton, cx, useNotifier } from "./base";

const TAILLES = {
  xs: "size-6 text-[10px]",
  sm: "size-8 text-[12px]",
  md: "size-10 text-sm",
  lg: "size-16 text-lg",
  xl: "size-24 text-2xl",
} as const;
export type TailleAvatar = keyof typeof TAILLES;

/** « Sophie Lambert » → « SL » ; « anne » → « A » ; vide → « ? ». */
export function initiales(nom: string): string {
  const mots = nom
    .trim()
    .split(/[\s-]+/)
    .filter((m) => /\p{L}/u.test(m));
  if (mots.length === 0) return "?";
  const premiere = (m: string) =>
    [...m.normalize("NFC")].find((c) => /\p{L}/u.test(c))!.toLocaleUpperCase("fr-FR");
  return (mots.length === 1 ? premiere(mots[0]!) : premiere(mots[0]!) + premiere(mots.at(-1)!)).slice(0, 2);
}

export function Avatar({
  nom,
  photo,
  taille = "sm",
  decoratif = true,
  className,
}: {
  nom: string;
  photo?: string | null;
  taille?: TailleAvatar;
  decoratif?: boolean;
  className?: string;
}) {
  const [echec, setEchec] = useState<string | null>(null);
  const base = cx(
    "inline-grid shrink-0 place-items-center overflow-hidden rounded-full select-none",
    TAILLES[taille],
    className,
  );
  if (photo && echec !== photo) {
    return (
      <img
        src={photo}
        alt={decoratif ? "" : `Photo de ${nom}`}
        width={256}
        height={256}
        loading="lazy"
        decoding="async"
        onError={() => setEchec(photo)}
        className={cx(base, "bg-papier-3 object-cover")}
        data-avatar="photo"
      />
    );
  }
  return (
    <span
      className={cx(base, "bg-accent font-semibold tracking-wide text-sur-accent")}
      role={decoratif ? undefined : "img"}
      aria-label={decoratif ? undefined : `Initiales de ${nom}`}
      aria-hidden={decoratif ? true : undefined}
      data-avatar="initiales"
    >
      {initiales(nom)}
    </span>
  );
}

/** Diamètre extérieur de l'anneau, par taille d'avatar (l'avatar + 2 × (trait + écart)). */
const ANNEAU: Partial<Record<TailleAvatar, { boite: number; trait: number }>> = {
  sm: { boite: 40, trait: 2.5 },
  md: { boite: 48, trait: 3 },
  lg: { boite: 76, trait: 4 },
  xl: { boite: 110, trait: 5 },
};

/**
 * Avatar entouré d'un anneau de progression (04/11/2026) : complétion du profil du formateur. Neutre tant que le
 * profil n'est pas complet, vert à 100 %. Accessible : `role="progressbar"`, `aria-valuenow`, « Profil complété à 70 % ».
 */
export function AvatarAnneau({ nom, photo, taille = "sm", pourcentage, className }: { nom: string; photo?: string | null; taille?: TailleAvatar; pourcentage: number; className?: string }) {
  const a = ANNEAU[taille] ?? ANNEAU.sm!;
  const p = Math.max(0, Math.min(100, Math.round(pourcentage)));
  const r = (a.boite - a.trait) / 2;
  const tour = 2 * Math.PI * r;
  const complet = p >= 100;
  return (
    <span
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={p}
      aria-label={`Profil complété à ${p} %`}
      data-anneau-profil={p}
      className={cx("relative inline-grid shrink-0 place-items-center", className)}
      style={{ width: a.boite, height: a.boite }}
    >
      <svg width={a.boite} height={a.boite} viewBox={`0 0 ${a.boite} ${a.boite}`} className="absolute inset-0 -rotate-90" aria-hidden focusable="false">
        <circle cx={a.boite / 2} cy={a.boite / 2} r={r} fill="none" stroke="var(--color-trait)" strokeWidth={a.trait} />
        <circle
          cx={a.boite / 2}
          cy={a.boite / 2}
          r={r}
          fill="none"
          stroke={complet ? "var(--color-valide)" : "var(--color-encre-2)"}
          strokeWidth={a.trait}
          strokeLinecap="round"
          strokeDasharray={tour}
          strokeDashoffset={tour * (1 - p / 100)}
          className="transition-[stroke-dashoffset] duration-500 ease-out motion-reduce:transition-none"
        />
      </svg>
      <Avatar nom={nom} photo={photo} taille={taille} />
    </span>
  );
}

/** Avatar du menu : avec l'anneau de complétion quand il y a un profil à compléter (formateur), sinon l'avatar seul. */
export function AvatarProfil({ nom, photo, pourcentage }: { nom: string; photo: string | null; pourcentage: number | null }) {
  return pourcentage === null ? <Avatar nom={nom} photo={photo} taille="sm" /> : <AvatarAnneau nom={nom} photo={photo} taille="sm" pourcentage={pourcentage} />;
}

const FORMATS_PHOTO = ".jpg,.jpeg,.png,.webp";

/**
 * Bloc « Ma photo » : aperçu, ajouter / remplacer, retirer. Pour tous les rôles (Mon compte, et Profil du formateur).
 * Le serveur recadre au carré, centré, et réduit à 256 px : aucune manipulation demandée à l'utilisateur.
 */
export function MaPhoto({ nom, photo }: { nom: string; photo: string | null }) {
  const requetes = useQueryClient();
  const notifier = useNotifier();
  const majMoi = (p: string | null) => {
    requetes.setQueryData<Moi>(["moi"], (m) => (m ? { ...m, photo: p } : m));
    // 04/11/2026 : la photo compte dans la complétude du profil (anneau, page « Mon profil »).
    void requetes.invalidateQueries({ queryKey: ["moi"] });
    void requetes.invalidateQueries({ queryKey: ["candidature"] });
  };
  const deposer = useMutation({
    mutationFn: (f: File) => api.fichier<{ photo: string | null }>("/compte/photo", f, {}),
    onSuccess: (r) => {
      majMoi(r.photo);
      notifier("succes", "Photo enregistrée.");
    },
    onError: (e) => notifier("danger", e instanceof ErreurApi ? e.message : "Envoi impossible. Réessayez."),
  });
  const retirer = useMutation({
    mutationFn: () => api.suppr<{ photo: null }>("/compte/photo"),
    onSuccess: () => {
      majMoi(null);
      notifier("succes", "Photo retirée.");
    },
  });
  const id = "photo-profil-fichier";
  return (
    <div className="flex flex-wrap items-center gap-4">
      <Avatar nom={nom} photo={photo} taille="xl" decoratif={false} />
      <div className="min-w-0 space-y-2">
        <p className="text-[13px] text-encre-2">
          JPG, PNG ou WebP, 5 Mo au plus. La photo est recadrée au carré, centrée, et sert à vous reconnaître
          dans l'application.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            id={id}
            type="file"
            accept={FORMATS_PHOTO}
            className="sr-only"
            tabIndex={-1}
            aria-label="Fichier de la photo de profil"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) deposer.mutate(f);
              e.target.value = "";
            }}
          />
          <Bouton
            variante="secondaire"
            taille="sm"
            enCours={deposer.isPending}
            icone={<Camera className="size-4" aria-hidden />}
            onClick={() => document.getElementById(id)?.click()}
          >
            {photo ? "Remplacer ma photo" : "Ajouter ma photo"}
          </Bouton>
          {photo && (
            <Bouton
              variante="discret"
              taille="sm"
              enCours={retirer.isPending}
              icone={<Trash2 className="size-4" aria-hidden />}
              onClick={() => confirm("Retirer votre photo de profil ?") && retirer.mutate()}
            >
              Retirer
            </Bouton>
          )}
        </div>
      </div>
    </div>
  );
}
